import { useState } from "react";
import { useAuth } from "./AuthContext";
import { useTheme } from "@mui/material";
import { Box, TextField, Button, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient, gql } from "graphql-request";
import { graphqlEndpoint } from "../config";
import logo from "../assets/logo.png";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import GroupsIcon from "@mui/icons-material/Groups";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { getDefaultRouteForRole } from "./getDefaultRouteForRole";

// Theme
import { tokens } from "../../src/theme/tokens";
import { LOGIN } from "../graph/tenants/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const Login = ({
  tenantBranding = null,
  previewMode = false,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const displayName = tenantBranding?.name || "Operational Clarity";
  const displayLogo = tenantBranding?.logoUrl || logo;
  const primaryColor =
    tenantBranding?.primaryColor || colors.scalpelTeal[400];
  const secondaryColor =
    tenantBranding?.secondaryColor || colors.houndGold[400];

  const { login } = useAuth();

  const handleLogin = async () => {
    if (previewMode) {
      return;
    }
    try {
      const data = await client.request(LOGIN, { input: { email, password } });
      const { token, user } = data.login;

      // Update context (this triggers re-renders everywhere)
      login(user, tenantBranding, token);

      navigate(getDefaultRouteForRole(user.role), { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed. Please check your credentials.");
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "420px",
          textAlign: "center",
          border: `1px solid ${secondaryColor}`,
          background: `linear-gradient(145deg, rgba(255,255,255,0.08), ${secondaryColor}22)`,
          boxShadow: `0 0 35px ${secondaryColor}33`,
        }}
      >
        
        <Typography
          variant="h3"
          fontWeight="bold"
          color={primaryColor}
          mb={2}
        >
          Welcome to
        </Typography>
        <img
          src={displayLogo}
          onError={(e) => {
            e.target.src = "/images/logos/operational-clarity.png";
          }}
          alt={`${displayName} Logo`}
          style={{
            width: 270,
            maxHeight: 120,
            objectFit: "contain",
          }}
        />
        <Box sx={{ mb: 3 }}>
            {[
              {
                text: "Clarity in your operations",
                icon: <TrackChangesIcon />,
              },
              {
                text: "Alignment in your teams",
                icon: <GroupsIcon />,
              },
              {
                text: "Confidence in your decisions",
                icon: <VerifiedUserIcon />,
              },
            ].map((item) => (
              <Box
                key={item.text}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={{ mb: 1 }}
              >
                <Box sx={{ color: primaryColor, display: "flex" }}>
                  {item.icon}
                </Box>

                <Typography
                  variant="body1"
                  fontWeight={700}
                  color={primaryColor}
                  sx={{ lineHeight: 1.5 }}
                >
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>

        <TextField
          disabled={previewMode}
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          disabled={previewMode}
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        {error && (
          <Typography color="error" variant="body2" mb={2}>
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleLogin}
          disabled={previewMode}
          sx={{
            bgcolor: primaryColor,
            "&:hover": {
              bgcolor: primaryColor,
              filter: "brightness(0.9)",
            },
          }}
        >
          Enter
        </Button>
      </Paper>
    </Box>
  );
};

export default Login;
